"use client";

import { Trans, useLingui } from "@lingui/react/macro";
import { RefreshCwIcon } from "lucide-react";
import type { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useChatPromptSurfaceContext } from "./chat-prompt-surface-context";

type ChatPromptSurfaceTitleProps = {
  children: ReactNode;
  className?: string;
  expandedClassName?: string;
  compactClassName?: string;
};

/**
 * `ChatPromptSurface` の展開状態に合わせて見出しの見た目を切り替える slot です。
 */
export function ChatPromptSurfaceTitle({
  children,
  className,
  expandedClassName,
  compactClassName,
}: ChatPromptSurfaceTitleProps) {
  const { isExpanded } = useChatPromptSurfaceContext("ChatPromptSurfaceTitle");

  return (
    <h1
      className={cn(
        className,
        isExpanded ? expandedClassName : compactClassName,
      )}
    >
      {children}
    </h1>
  );
}

type ChatPromptSurfaceSuggestionsProps = {
  prompts: string[];
  isGenerating: boolean;
  onRefresh: () => void;
  className?: string;
  expandedClassName?: string;
  compactClassName?: string;
};

/**
 * 新規チャット開始画面で表示する候補プロンプト一覧です。
 *
 * 候補の選択は context 経由で draft text に反映します。送信そのものは
 * `ChatPromptSurfaceInput` に任せるため、候補クリックだけで送信されることはありません。
 */
export function ChatPromptSurfaceSuggestions({
  prompts,
  isGenerating,
  onRefresh,
  className,
  expandedClassName,
  compactClassName,
}: ChatPromptSurfaceSuggestionsProps) {
  const { t } = useLingui();
  const { isExpanded, setInput } = useChatPromptSurfaceContext(
    "ChatPromptSurfaceSuggestions",
  );

  return (
    <div
      className={cn(
        className,
        isExpanded ? expandedClassName : compactClassName,
      )}
    >
      <div className="flex items-center justify-end">
        <Button
          variant="ghost"
          size="sm"
          className="h-7 gap-1.5 px-2 text-xs text-muted-foreground"
          onClick={onRefresh}
          disabled={isGenerating}
          aria-label={t`Refresh suggestions`}
        >
          <RefreshCwIcon
            className={cn("h-3 w-3", isGenerating && "animate-spin")}
          />
          <Trans>Refresh</Trans>
        </Button>
      </div>
      <div
        className={cn(
          "grid grid-cols-1 sm:grid-cols-2",
          isExpanded ? "gap-3" : "gap-2",
        )}
      >
        {prompts.map((prompt, index) => (
          <Button
            key={`${prompt}-${index}`}
            variant="secondary"
            className={cn(
              "h-auto w-full justify-start whitespace-normal rounded-lg text-left text-sm shadow-none",
              isExpanded
                ? "min-h-14 px-4 py-3"
                : "min-h-11 px-3 py-2.5 text-muted-foreground",
              isGenerating && "animate-pulse",
            )}
            onClick={() => setInput(prompt)}
            disabled={isGenerating}
          >
            {prompt}
          </Button>
        ))}
      </div>
    </div>
  );
}
